import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImagePlus, ArrowRight } from "lucide-react";
import { GALLERY } from "../../lib/site-data";

const FILTERS = [
    { id: "henna", label: "Henna", hint: "Hand-drawn designs" },
    { id: "face", label: "Face Painting", hint: "Vibrant transformations" },
    { id: "glitter", label: "Glitter Tattoos", hint: "Sparkle that lasts" },
];

export const Gallery = () => {
    const [active, setActive] = useState(null);
    const items = active ? GALLERY.filter((i) => i.category === active) : [];

    return (
        <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-brand-parchment">
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div className="text-center mb-12">
                    <p className="text-xs tracking-[0.25em] uppercase text-brand-coral mb-4">Recent work</p>
                    <h2 className="font-display text-4xl sm:text-6xl text-brand-ink leading-[1.05] max-w-3xl mx-auto">
                        A few of my favorite <em className="text-brand-coral">moments</em>.
                    </h2>
                    <p className="mt-4 text-brand-inkSoft max-w-xl mx-auto text-sm">
                        Pick a category to peek inside.
                    </p>
                </div>

                {/* Category cards — clickable reveal */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                    {FILTERS.map((f, i) => {
                        const isActive = active === f.id;
                        const sample = GALLERY.find((g) => g.category === f.id);
                        return (
                            <motion.button
                                key={f.id}
                                onClick={() => setActive(isActive ? null : f.id)}
                                data-testid={`gallery-filter-${f.id}`}
                                whileHover={{ y: -4 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className={`group relative rounded-2xl overflow-hidden border-2 transition-all duration-500 text-left ${
                                    isActive
                                        ? "border-brand-coral shadow-2xl"
                                        : "border-brand-border hover:border-brand-coral/60"
                                }`}
                            >
                                <div className="relative h-40 sm:h-44 overflow-hidden bg-brand-cream">
                                    {sample && (
                                        <img
                                            src={sample.src}
                                            alt={f.label}
                                            className={`w-full h-full object-cover transition-all duration-700 ${
                                                isActive ? "scale-110 opacity-90" : "scale-100 opacity-70 group-hover:scale-105 group-hover:opacity-95"
                                            }`}
                                        />
                                    )}
                                    <div className={`absolute inset-0 transition-opacity duration-500 ${
                                        isActive ? "bg-gradient-to-t from-brand-ink/85 via-brand-ink/30 to-transparent" : "bg-gradient-to-t from-brand-ink/70 via-brand-ink/20 to-transparent group-hover:from-brand-ink/80"
                                    }`} />
                                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                                        <p className="text-xs tracking-[0.25em] uppercase text-white/80 mb-1">{f.hint}</p>
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight">
                                                {f.label}
                                            </h3>
                                            <span
                                                className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
                                                    isActive
                                                        ? "bg-brand-coral text-white rotate-45"
                                                        : "bg-white/90 text-brand-ink group-hover:bg-brand-coral group-hover:text-white"
                                                }`}
                                            >
                                                {isActive ? <ArrowRight className="h-4 w-4 -rotate-45" /> : <ImagePlus className="h-4 w-4" />}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Photos reveal */}
                <AnimatePresence mode="wait">
                    {active && (
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.5 }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pt-2">
                                {items.map((it, i) => (
                                    <motion.figure
                                        key={it.src}
                                        initial={{ opacity: 0, scale: 0.92, y: 20 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.45, delay: i * 0.05 }}
                                        className="relative overflow-hidden rounded-2xl group bg-brand-cream aspect-square"
                                        data-testid={`gallery-item-${i}`}
                                    >
                                        <img
                                            src={it.src}
                                            alt={it.alt}
                                            className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        <figcaption className="absolute bottom-3 left-3 right-3 text-white text-xs tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            {it.alt}
                                        </figcaption>
                                    </motion.figure>
                                ))}
                            </div>
                            <div className="text-center mt-8">
                                <button
                                    onClick={() => setActive(null)}
                                    data-testid="gallery-close"
                                    className="text-sm text-brand-inkSoft hover:text-brand-coral transition-colors underline underline-offset-4 decoration-brand-coral/40"
                                >
                                    Close gallery
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default Gallery;
